import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import api from '../lib/api';

interface Order {
  id: string;
  order_number?: string;
  platform?: string;
  market: string;
  status: string;
  items?: any; // the backend returns different structures based on endpoint
  total: string;
  customer?: string | any;
  time: string;
}

export default function Orders() {
  const queryClient = useQueryClient();

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const response = await api.get<Order[]>('/orders');
      return response.data;
    },
  });

  const fulfillMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await api.post(`/orders/${orderId}/fulfill`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4 text-red-500">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load orders. Please check if the API is running.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Orders</h1>
        <p className="text-gray-400 mt-1">Manage and fulfill orders</p>
      </div>

      <div className="card">
        {Array.isArray(orders) && orders.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm border-b border-dark-700">
                <th className="pb-3 font-medium">Order ID</th>
                <th className="pb-3 font-medium">Platform</th>
                <th className="pb-3 font-medium">Market</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Items</th>
                <th className="pb-3 font-medium">Total</th>
                <th className="pb-3 font-medium">Customer</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {orders.map((order) => {
                const isPending = order.status === 'Pending' || order.status === 'pending_fulfillment';
                const isFulfilling = fulfillMutation.variables === order.id && fulfillMutation.isPending;

                return (
                  <tr key={order.id} className="border-b border-dark-800">
                    <td className="py-3 font-medium text-white">{order.id}</td>
                    <td className="py-3">{order.platform || 'Unknown'}</td>
                    <td className="py-3">{order.market}</td>
                    <td className="py-3">
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${isPending ? 'bg-yellow-500/20 text-yellow-400' : ''}
                        ${order.status === 'Shipped' || order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' : ''}
                        ${order.status === 'Delivered' || order.status === 'delivered' ? 'bg-green-500/20 text-green-400' : ''}
                      `}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3">{Array.isArray(order.items) ? order.items.length : (order.items || '-')}</td>
                    <td className="py-3 font-medium">{order.total}</td>
                    <td className="py-3">
                      <div>
                        <p className="text-white">
                           {typeof order.customer === 'string' ? order.customer : (order.customer?.name || 'Customer')}
                        </p>
                        <p className="text-xs text-gray-500">{order.time}</p>
                      </div>
                    </td>
                    <td className="py-3">
                      {isPending && (
                        <button
                          onClick={() => fulfillMutation.mutate(order.id)}
                          disabled={isFulfilling}
                          className="btn-primary text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isFulfilling ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Fulfill'
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="py-12 text-center">
            <p className="text-gray-400">No orders found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
